import { defineComponent, PropType } from 'vue';
import { product_search_product } from '../../objects/v1_3/product/search/product_search_product';
import { Booking } from '../../services/booking';
import { Product } from '../../services/product';
import { product_search } from '../../objects/v1_3/product/search/product_search';
import DualRangeSlider from './dual_range_slider.vue';

const Book = new Booking();
const Prod = new Product();

type SectionKey =
  | 'sortBy' | 'search' | 'brands' | 'departingFrom' | 'duration' | 'price'
  | 'dates'  | 'accommodation' | 'groupSize' | 'destinations' | 'highlights';

// display name for a tour's brand code (product.brand), matching the strict filters (tour_list/strict_filters.ts);
// unknown codes are shown as they are
const brandName = (brand: string): string => {
  const code = brand.toUpperCase();
  if (code.includes('HEX'))  return 'Highland Explorer Tours';
  if (code.includes('HAG'))  return 'Haggis Adventures';
  if (code.includes('SEIT')) return 'SEIT Tours';
  if (code.includes('AAT'))  return 'AAT Kings';
  return brand;
};

// brand mark for the brand filter tiles (same images as the tour cards' brand logos, tour_card.ts); '' = show initials
const brandLogo = (brand: string): string => {
  const code = brand.toUpperCase();
  if (code.includes('HAG')) return 'https://contenthub.radicaltravel.com/media/2629/hagfortab.png?width=96&mode=max&animationprocessmode=first';
  if (code.includes('HEX')) return 'https://contenthub.radicaltravel.com/media/2659/hexfortab2.jpg?width=96&mode=max&animationprocessmode=first';
  return '';
};

// yyyy-mm-dd in local time (toDateString() below goes through UTC, which is a day behind east of UTC around midnight)
const localDate = (d: Date): string =>
  d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');

const shiftDate = (date: string, days: number): string => {
  const parts = date.split('-').map(Number);
  return localDate(new Date(parts[0], parts[1] - 1, parts[2] + days));
};

export default defineComponent({
  name: 'TourFilterPanel',
  components: { DualRangeSlider },

  props: {
    tours: {
      type: Array as PropType<product_search_product[]>,
      required: true,
      default: () => [],
    },
  },

  emits: ['filtered', 'chipsChange'],

  data() {
    return {
      drawerOpen: false as boolean,
      openRoomInfo: '' as string,
      sectionsOpen: {
        sortBy:        false,
        search:        false,
        brands:        false,
        departingFrom: false,
        duration:      false,
        price:         false,
        dates:         false,
        accommodation: false,
        groupSize:     false,
        destinations:  false,
        highlights:    false,
      } as Record<SectionKey, boolean>,

      // Filter values
      searchInput:    '' as string,
      searchTimer:    null as ReturnType<typeof setTimeout> | null,
      brands:         [] as string[],
      departingFrom:  '' as string,
      durationMin:    0  as number,
      durationMax:    0  as number,
      priceMin:       0  as number,
      priceMax:       0  as number,
      // 'single': one travel date, searches that day only (the default).
      // 'range': from/to; an empty "to" means from that date onwards, an empty "from" means from today.
      dateMode:       'single' as 'single' | 'range',
      dateFrom:       '' as string,
      dateTo:         '' as string,
      dateTimer:      null as ReturnType<typeof setTimeout> | null,
      dateLoading:    false as boolean,
      dateRequest:    0 as number,
      accommodation:  [] as string[],
      groupSize:      '' as string,
      destinations:   [] as string[],
      highlights:     [] as string[],
      sortBy: '' as string,

      // Derived bounds
      durationBounds: { min: 0, max: 0 } as { min: number; max: number },
      priceBounds:    { min: 0, max: 0 } as { min: number; max: number },

      // Date availability result
      availableTourIds: null as string[] | null,

      // Cached prices keyed by tour_id
      priceCache: {} as Record<string, number>,
    };
  },

  computed: {
    sortSummary(): string | null {
        const labels: Record<string, string> = {
            'price_desc': 'Price: High to Low',
            'price_asc':  'Price: Low to High',
            'duration_asc': 'Shortest First',
            'duration_desc': 'Longest First',
        };
        return this.sortBy ? labels[this.sortBy] : null;
    },
    accommodationOptions(): string[] {
        const seen = new Set<string>();
        this.tours.forEach(t => {
            t.prices?.forEach(p => {
                p.amounts?.forEach((a: any) => {
                    if (a.room_type) seen.add(a.room_type);
                });
            });
        });
        return Array.from(seen).sort();
    },
    // brand filter: agents only (agent portals sell several brands), and only when the results hold more than one brand
    brandOptions(): { value: string; label: string; logo: string; initials: string; count: number }[] {
      const counts = new Map<string, number>();
      this.tours.forEach(t => { if (t.brand) counts.set(t.brand, (counts.get(t.brand) ?? 0) + 1); });
      return Array.from(counts.entries())
        .map(([value, count]) => {
          const label = brandName(value);
          return { value, label, logo: brandLogo(value), initials: label.split(/\s+/).map(w => w.charAt(0)).join('').slice(0, 2).toUpperCase(), count };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    showBrandFilter(): boolean {
      return this.$store.getters.get_Agent_Login?.valid_login == true && this.brandOptions.length > 1;
    },
    brandSummary(): string | null {
      if (!this.brands.length) return null;
      return this.brandOptions.filter(o => this.brands.includes(o.value)).map(o => o.label).join(', ');
    },
    departureCities(): string[] {
      const seen = new Set<string>();
      this.tours.forEach(t => { if (t.start_location?.city) seen.add(t.start_location.city); });
      return Array.from(seen).sort();
    },

    // Dynamic options — empty until API adds the fields
    destinationOptions(): string[] {
      const seen = new Set<string>();
      this.tours.forEach(t => {
        ((t as any).destinations as string[] | undefined)?.forEach(d => seen.add(d));
      });
      return Array.from(seen).sort();
    },
    highlightOptions(): string[] {
      const seen = new Set<string>();
      this.tours.forEach(t => {
        ((t as any).highlights as string[] | undefined)?.forEach(h => seen.add(h));
      });
      return Array.from(seen).sort();
    },

    hasActiveFilters(): boolean {
      return (
        this.searchInput.trim()       !== ''                   ||
        this.brands.length            > 0                      ||
        this.departingFrom            !== ''                   ||
        this.durationMin              !== this.durationBounds.min ||
        this.durationMax              !== this.durationBounds.max ||
        this.priceMin                 !== this.priceBounds.min ||
        this.priceMax                 !== this.priceBounds.max ||
        this.dateFrom                 !== ''                   ||
        this.dateTo                   !== ''                   ||
        this.accommodation.length     > 0                      ||
        this.groupSize                !== ''                   ||
        this.destinations.length      > 0                      ||
        this.highlights.length        > 0
      );
    },

    // Active filters as removable pills, shown above the results by the search page (1_search.vue).
    // Multi-value filters (brands, accommodation) get a pill per value; the dates are a separate control (dateChip).
    activeChips(): { section: SectionKey; value?: string; label: string }[] {
      const chips: { section: SectionKey; value?: string; label: string }[] = [];
      const term = this.searchInput.trim();
      if (term) chips.push({ section: 'search', label: `“${term}”` });
      this.brands.forEach(b => chips.push({ section: 'brands', value: b, label: brandName(b) }));
      if (this.departingFrom) chips.push({ section: 'departingFrom', label: `From ${this.departingFrom}` });
      if (this.durationSummary) chips.push({ section: 'duration', label: this.durationSummary });
      if (this.priceSummary) chips.push({ section: 'price', label: this.priceSummary });
      this.accommodation.forEach(a => chips.push({ section: 'accommodation', value: a, label: a === '' ? 'Day Tours' : a }));
      return chips;
    },
    dateChip(): { label: string; canStep: boolean; canStepBack: boolean } | null {
      if (!this.dateSummary) return null;
      return { label: this.dateSummary, canStep: this.dateFrom !== '', canStepBack: this.canStepDatesBack };
    },
    chipState(): { chips: { section: SectionKey; value?: string; label: string }[]; date: { label: string; canStep: boolean; canStepBack: boolean } | null; sort: string } {
      return { chips: this.activeChips, date: this.dateChip, sort: this.sortBy };
    },

    // Section summaries (null = no active value = no chip shown)
    // the sliders snap to lengths and prices the tours actually have, so a single value always matches tours
    durationValues(): number[] {
      return this.tours.map(t => this.getTourDurationInDays(t));
    },
    priceValues(): number[] {
      return Object.values(this.priceCache as Record<string, number>).filter(p => p > 0).map(p => Math.floor(p));
    },
    durationSummary(): string | null {
      if (this.durationMin === this.durationBounds.min && this.durationMax === this.durationBounds.max) return null;
      if (this.durationMin === this.durationMax) return `${this.durationMin} ${this.durationMin === 1 ? 'day' : 'days'}`;
      return `${this.durationMin}–${this.durationMax} days`;
    },
    priceSummary(): string | null {
      if (this.priceMin === this.priceBounds.min && this.priceMax === this.priceBounds.max) return null;
      const sym = this.currencySymbol;
      if (this.priceMin === this.priceMax) return `${sym}${this.priceMin}`;
      return `${sym}${this.priceMin} – ${sym}${this.priceMax}`;
    },
    dateSummary(): string | null {
      if (!this.dateFrom && !this.dateTo) return null;
      const from = this.dateFrom ? this.formatDate(this.dateFrom) : null;
      const to   = this.dateTo   ? this.formatDate(this.dateTo)   : null;
      if (from && to && this.dateFrom === this.dateTo) return from;
      if (from && to) return `${from} → ${to}`;
      if (from) return this.dateMode === 'single' ? from : `From ${from} onwards`;
      return `Until ${to}`;
    },
    accommodationSummary(): string | null {
        if (!this.accommodation.length) return null;
        return this.accommodation.map(a => a === '' ? 'Day Tours' : a).join(', ');
    },
    groupSizeSummary(): string | null {
      if (!this.groupSize) return null;
      return `Max ${this.groupSize}`;
    },

    currencySymbol(): string {
        const mask = this.$store.getters.get_Currency?.currency_info?.currency_mask ?? '';
        return mask.charAt(0);
    },

    // Date input bounds
    dateFromMin(): string { return localDate(new Date()); },
    dateFromMax(): string {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 2);
      return localDate(d);
    },
    dateTomorrow(): string { return shiftDate(localDate(new Date()), 1); },
    // the end date may be the same as the start date, to search a single day
    dateToMin(): string {
      return this.dateFrom || this.dateFromMin;
    },
    // previous/next day buttons next to the date summary; can't step the start date before today
    canStepDatesBack(): boolean {
      return this.dateFrom !== '' && this.dateFrom > localDate(new Date());
    },
  },

  methods: {
    // ── Bounds initialisation ──────────────────────────────────────────────

    initialiseBounds() {
      this.buildPriceCache();
      this.setDurationBounds();
      this.setPriceBounds();
    },

    buildPriceCache() {
      const cache: Record<string, number> = {};
      this.tours.forEach(t => {
        cache[t.tour_id] = Prod.product_pricing(
          Book.Pricing_Age_Category(this.$store),
          this.$store.getters.get_Currency,
          t
        );
      });
      this.priceCache = cache;
    },

    toggleRoomInfo(opt: string) {
        this.openRoomInfo = this.openRoomInfo === opt ? '' : opt;
    },

    getRoomDescription(room: string): string {
        if (room === '') return 'Tours that return on the same day with no overnight stay.';
        const r = room.toLowerCase();
        if (r.includes('private quad'))   return 'Private Quad Room: Private hostel room, suitable for 4 people.';
        if (r.includes('private triple')) return 'Private Triple Room: Private hostel room, suitable for 3 people.';
        if (r.includes('private twin'))   return 'Private Twin Room: Private hostel room, suitable for 2 people.';
        if (r.includes('twin'))           return 'Twin Room: 1 room with 2 single beds. Suitable for 2 people, one bed each.';
        if (r.includes('double'))         return 'Double Room: 1 room with 1 double bed. Suitable for 2 people to share.';
        if (r.includes('single'))         return 'Single Room: 1 room with 1 single bed. Suitable for 1 person. Single occupancy supplement applies.';
        if (r.includes('family 3'))       return 'Family 3 Room: 1 room with 1 double bed and 1 single bed. Suitable for 3 people.';
        if (r.includes('family 4'))       return 'Family 4 Room: 1 room with 1 double bed and 2 single beds. Suitable for 4 people.';
        if (r.includes('dorm'))           return 'Dorm Bed: 1 mixed-gender hostel room shared with multiple travellers, one bed each.';
        return '';
    },

    setDurationBounds() {
        if (!this.tours.length) return;
        const days = this.tours.map(t => this.getTourDurationInDays(t));
        const min = Math.min(...days);
        const max = Math.max(...days);
        this.durationBounds = { min, max };
        if (this.durationMin === 0 || this.durationMin < min) this.durationMin = min;
        if (this.durationMax === 0 || this.durationMax > max) this.durationMax = max;
    },

    setPriceBounds() {
      const prices = Object.values(this.priceCache).filter(p => p > 0);
      if (!prices.length) return;
      const min = Math.floor(Math.min(...prices));
      const max = Math.ceil(Math.max(...prices));
      this.priceBounds = { min, max };
      if (this.priceMin === 0 || this.priceMin < min) this.priceMin = min;
      if (this.priceMax === 0 || this.priceMax > max) this.priceMax = max;
  },

    getTourDurationInDays(tour: product_search_product): number {
        let days = tour.duration?.days ?? 0;
        if ((tour.duration?.hours ?? 0) >= 1) {
            days++;
        }
        return Math.max(days, 1);
    },

    // ── Search ────────────────────────────────────────────────────────────

    onSearchInput(e: Event) {
      this.searchInput = (e.target as HTMLInputElement).value;
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.applyFilters(), 300);
    },

    // ── Date availability ─────────────────────────────────────────────────

    onDateChange() {
      if (!this.dateFrom && !this.dateTo) {
        this.availableTourIds = null;
        this.applyFilters();
        return;
      }
      if (this.dateTimer) clearTimeout(this.dateTimer);
      this.dateTimer = setTimeout(() => this.fetchDateAvailability(), 400);
    },

    // a departure picked in a tour's booking calendar (via 1_search.vue): make it the travel date, unless it's already
    // inside the selected range, so a deliberately chosen range isn't thrown away
    setTravelDate(output_date: string) {
      if (!output_date) return;

      const inRange = this.dateMode === 'range' && this.dateFrom !== ''
        && output_date >= this.dateFrom && (this.dateTo === '' || output_date <= this.dateTo);
      if (inRange) return;
      if (this.dateMode === 'single' && this.dateFrom === output_date) return;

      this.dateMode = 'single';
      this.dateTo   = '';
      this.dateFrom = output_date;        // the date watchers search again
    },

    // dates applied in the calendar slide-out (via 1_search.vue): one day = single-day mode, two = range, none = cleared
    setDates(output_from: string, output_to: string) {
      if (!output_from) {
        this.dateTo   = '';
        this.dateFrom = '';
        return;
      }

      if (!output_to || output_to === output_from) {
        this.dateMode = 'single';
        this.dateTo   = '';
      } else {
        this.dateMode = 'range';
        this.dateTo   = output_to;
      }
      this.dateFrom = output_from;          // the date watchers search again
    },

    // switch between a single travel date and a date range; the "to" date only exists in range mode
    setDateMode(mode: 'single' | 'range') {
      if (this.dateMode === mode) return;
      this.dateMode = mode;
      if (this.dateTo) {
        this.dateTo = '';           // the dateTo watcher searches again
      } else if (this.dateFrom) {
        this.onDateChange();        // same date, different meaning (that day vs. onwards)
      }
    },

    // move the selected dates (start, and end when set) a day back or forward; the watchers below search again
    stepDates(days: number) {
      if (!this.dateFrom) return;
      if (days < 0 && !this.canStepDatesBack) return;

      // end first, so the dateFrom watcher never sees a start after the end
      if (this.dateTo) this.dateTo = shiftDate(this.dateTo, days);
      this.dateFrom = shiftDate(this.dateFrom, days);
    },

    async fetchDateAvailability() {
        // stepping the dates quickly can overlap requests; only the latest may set the result
        const request = ++this.dateRequest;
        this.dateLoading = true;
        try {
            // the API's departure_date_to is the start (midnight) of that day, so the search always runs up to the day
            // after the last day wanted
            let from = this.dateFrom;
            let to   = '';

            if (this.dateTo) {
                to = shiftDate(this.dateTo, 1);
                if (!from) from = localDate(new Date());        // range "until": from today
            } else if (this.dateMode === 'single') {
                to = shiftDate(from, 1);                        // single day
            } else {
                to = shiftDate(from, 365);                      // range "onwards": the next year
            }

            const result = await Prod.Get_Product_Search_Date_Range([], from, to);
            if (request !== this.dateRequest) return;
            this.availableTourIds = result.products.map(p => p.tour_id);
        } catch (err) {
            if (request !== this.dateRequest) return;
            console.error('[TourFilterPanel] Date availability error:', err);
            this.availableTourIds = null;
        } finally {
            if (request === this.dateRequest) {
                this.dateLoading = false;
                this.applyFilters();
            }
        }
    },

    // ── Core filter logic ─────────────────────────────────────────────────

    applyFilters() {
      let result = [...this.tours];

      // Search (debounced, stop words stripped)
      const term = this.searchInput.trim().toLowerCase();
      if (term) {
        const stop  = new Set(['the','a','an','and','or','but','in','on','at','to','for','of','with','by','from','is','it','its','this','that','are','was','were']);
        const terms = term.split(/\s+/).filter(w => !stop.has(w) && w.length > 1);
        if (terms.length) {
          result = result.filter(t => {
            const hay = `${t.name} ${t.description_headline} ${t.description_short} ${t.description}`.toLowerCase();
            return terms.every(w => hay.includes(w));
          });
        }
      }

      // Brand (agents)
      if (this.brands.length > 0) {
        result = result.filter(t => this.brands.includes(t.brand));
      }

      // Departing from
      if (this.departingFrom) {
        result = result.filter(t => t.start_location?.city === this.departingFrom);
      }

      // Duration
      if (this.durationMin !== this.durationBounds.min || this.durationMax !== this.durationBounds.max) {
        result = result.filter(t => {
            const d = this.getTourDurationInDays(t);
            return d >= this.durationMin && d <= this.durationMax;
        });
      }

      // Price
      if (this.priceMin !== this.priceBounds.min || this.priceMax !== this.priceBounds.max) {
        result = result.filter(t => {
          // compare on whole units like the slider, so a single value (e.g. 499) still matches a 499.50 tour
          const p = Math.floor(this.priceCache[t.tour_id] ?? 0);
          return p >= this.priceMin && p <= this.priceMax;
        });
      }

      // Date availability
      if (this.availableTourIds !== null) {
        const ids = new Set(this.availableTourIds.map(id => id.toUpperCase()));
        result = result.filter(t => ids.has((t.tour_id ?? '').toUpperCase()));
      }

      // Accommodation — passes through if API field absent
        if (this.accommodation.length > 0) {
            result = result.filter(t => {
                const types = (t as any).accommodationTypes as string[] | undefined;

                if (types === undefined) {
                    const hasRoomTypes = t.prices?.some(p =>
                        p.amounts?.some((a: any) => a.room_type)
                    ) ?? false;
                    const isDayTour = !hasRoomTypes;

                    if (isDayTour) return this.accommodation.includes('');

                    const tourRoomTypes = new Set<string>();
                    t.prices?.forEach(p => {
                        p.amounts?.forEach((a: any) => {
                            if (a.room_type) tourRoomTypes.add(a.room_type);
                        });
                    });
                    return this.accommodation.some(a => tourRoomTypes.has(a));
                }

                return this.accommodation.some(a => types.includes(a));
            });
        }

      // Group size — passes through if API field absent
      if (this.groupSize) {
        result = result.filter(t => {
          const size = (t as any).groupSize as string | undefined;
          return size === undefined ? true : size === this.groupSize;
        });
      }

      // Destinations — passes through if API field absent
      if (this.destinations.length > 0) {
        result = result.filter(t => {
          const dests = (t as any).destinations as string[] | undefined;
          return dests === undefined ? true : this.destinations.some(d => dests.includes(d));
        });
      }

      // Highlights — passes through if API field absent
      if (this.highlights.length > 0) {
        result = result.filter(t => {
          const hl = (t as any).highlights as string[] | undefined;
          return hl === undefined ? true : this.highlights.some(h => hl.includes(h));
        });
      }

      if (this.sortBy) {
        result = [...result].sort((a, b) => {
            if (this.sortBy === 'price_asc')      return (this.priceCache[a.tour_id] ?? 0) - (this.priceCache[b.tour_id] ?? 0);
            if (this.sortBy === 'price_desc')     return (this.priceCache[b.tour_id] ?? 0) - (this.priceCache[a.tour_id] ?? 0);
            if (this.sortBy === 'duration_asc')   return this.getTourDurationInDays(a) - this.getTourDurationInDays(b);
            if (this.sortBy === 'duration_desc')  return this.getTourDurationInDays(b) - this.getTourDurationInDays(a);
            return 0;
        });
    }

      this.$emit('filtered', result);
    },

    // ── Clear ─────────────────────────────────────────────────────────────

    clearAll() {
      this.searchInput    = '';
      this.brands         = [];
      this.departingFrom  = '';
      this.durationMin    = this.durationBounds.min;
      this.durationMax    = this.durationBounds.max;
      this.priceMin       = this.priceBounds.min;
      this.priceMax       = this.priceBounds.max;
      this.dateFrom       = '';
      this.dateTo         = '';
      this.accommodation  = [];
      this.groupSize      = '';
      this.destinations   = [];
      this.highlights     = [];
      this.availableTourIds = null;
      // the sort order is not a filter: clearing the filters keeps it
      this.applyFilters();
    },

    // the Sort by dropdown in the results bar (1_search.vue)
    setSort(value: string) {
      this.sortBy = value;
    },

    clearSection(section: SectionKey) {
      ({
        search:        () => { this.searchInput = ''; },
        brands:        () => { this.brands = []; },
        departingFrom: () => { this.departingFrom = ''; },
        duration:      () => { this.durationMin = this.durationBounds.min; this.durationMax = this.durationBounds.max; },
        price:         () => { this.priceMin = this.priceBounds.min; this.priceMax = this.priceBounds.max; },
        dates:         () => { this.dateFrom = ''; this.dateTo = ''; this.availableTourIds = null; },
        accommodation: () => { this.accommodation = []; },
        groupSize:     () => { this.groupSize = ''; },
        destinations:  () => { this.destinations = []; },
        highlights:    () => { this.highlights = []; },
        sortBy: () => { this.sortBy = ''; },
      } as Record<SectionKey, () => void>)[section]?.();
      this.applyFilters();
    },

    // remove one pill: a single value of a multi-value filter, or the whole section
    removeChip(section: SectionKey, value?: string) {
      if (section === 'brands' && value !== undefined) {
        this.brands = this.brands.filter(b => b !== value);
      } else if (section === 'accommodation' && value !== undefined) {
        this.accommodation = this.accommodation.filter(a => a !== value);
      } else {
        this.clearSection(section);
      }
    },

    // the date pill's calendar button: open the Travel Dates section (in the drawer on small screens)
    openDates() {
      if (!this.sectionsOpen.dates) this.toggleSection('dates');
      if (window.innerWidth < 1024) {
        this.openDrawer();
      }
      this.$nextTick(() => {
        (this.$el?.parentElement?.querySelector?.('.tfp-date-input') as HTMLInputElement | null)?.focus();
      });
    },

    // accordion: opening a section closes the others, so the panel doesn't grow a scrollbar
    toggleSection(section: SectionKey) {
      const open = !this.sectionsOpen[section];
      (Object.keys(this.sectionsOpen) as SectionKey[]).forEach(key => { this.sectionsOpen[key] = false; });
      this.sectionsOpen[section] = open;
    },

    // open the browser's date picker when the field itself is clicked, not just its small calendar icon
    openPicker(e: Event) {
      const input = e.target as HTMLInputElement & { showPicker?: () => void };
      try { input.showPicker?.(); } catch { /* not supported, or not allowed without a user gesture: the field still works */ }
    },
    pickDay(days: number) {
      this.dateFrom = shiftDate(localDate(new Date()), days);
    },

    // ── Drawer ────────────────────────────────────────────────────────────

    openDrawer()  { this.drawerOpen = true;  document.body.style.overflow = 'hidden'; },
    closeDrawer() { this.drawerOpen = false; document.body.style.overflow = '';       },
    onEscape(e: KeyboardEvent) { if (e.key === 'Escape') this.closeDrawer(); },

    // ── Helpers ───────────────────────────────────────────────────────────

    toDateString(d: Date): string {
      return d.toISOString().split('T')[0];
    },
    formatDate(str: string): string {
      try {
        return new Date(str).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
      } catch { return str; }
    },
  },

  watch: {
    tours: {
      handler() { 
            this.initialiseBounds(); 
        },
    },
    chipState: {
      handler(state) { this.$emit('chipsChange', state); },
      deep: true,
      immediate: true,
    },
    brands()             { this.applyFilters(); },
    departingFrom()      { this.applyFilters(); },
    durationMin(val)     { this.applyFilters(); },
    durationMax()        { this.applyFilters(); },
    priceMin()           { this.applyFilters(); },
    priceMax()           { this.applyFilters(); },
    accommodation()      { this.applyFilters(); },
    groupSize()          { this.applyFilters(); },
    destinations()       { this.applyFilters(); },
    highlights()         { this.applyFilters(); },
    sortBy() { this.applyFilters(); },
    dateFrom(val) {
        if (val && this.dateTo && val > this.dateTo) {
            this.dateTo = '';
        }
        this.onDateChange();
    },
    dateTo() { 
        this.onDateChange(); 
    },
  },

    mounted() {
        this.initialiseBounds();
        window.addEventListener('keydown', this.onEscape);
    },
    unmounted() { 
        window.removeEventListener('keydown', this.onEscape); 
        document.body.style.overflow = ''; 
    },
});